import { useMutation, useQueryClient } from '@tanstack/react-query'
import { runDetached } from '@/shared/lib/runDetachedPromise'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import { queryKeys } from '@/api/query-keys'
import { cacheSurveySession } from './survey-execution-mutations'
import { SpSurveyPath } from '../lib/sp-survey-paths'
import { isPendingSurveyUser } from '../lib/pending-survey-session'
import { useSessionStore } from '@/stores/useSessionStore'
import type { ProviderResponseState, ProviderStartResponseData } from '../lib/types'

export function useStartProviderResponse() {
  const queryClient = useQueryClient()
  const pendingUser = useSessionStore((s) => isPendingSurveyUser(s.user))

  return useMutation({
    mutationFn: async (instrumentId: string) => {
      const { data } = await apiClient.post<ApiResponse<ProviderStartResponseData>>(
        pendingUser
          ? SpSurveyPath.pendingStart(instrumentId)
          : SpSurveyPath.responsesStart(instrumentId),
      )
      return data.data
    },
    onSuccess: (data, instrumentId) => {
      if (data?.response) {
        cacheSurveySession(queryClient, instrumentId, data.response.responseId, {
          response: data.response,
          definition: data.definition,
          resumed: data.resumed,
        })
      }
      runDetached(
        queryClient.invalidateQueries({ queryKey: queryKeys.spInstrument.status(instrumentId) }),
      )
    },
  })
}

export function useStartOverProviderResponse() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { responseId: string; instrumentId: string }) => {
      const { data } = await apiClient.post<ApiResponse<ProviderResponseState>>(
        SpSurveyPath.responseStartOver(input.responseId),
      )
      return data.data
    },
    onSuccess: (_data, input) => {
      runDetached(
        queryClient.invalidateQueries({
          queryKey: queryKeys.spInstrument.status(input.instrumentId),
        }),
      )
    },
  })
}
